import { useEffect, useCallback } from 'react';
import { CheckCircle2, XCircle, Upload, X } from 'lucide-react';

import useUpload from '../../../../shared/hooks/useUpload';
import useTranslation from '../../../../shared/hooks/useTranslation';
import cn from '../../../../shared/utils/classNames';

import styles from './UploadProgress.module.css';

export const UploadProgress = () => {
  const { uploadState, updateUploadState } = useUpload();
  const { t } = useTranslation();
  const { 
    isUploading, 
    uploadProgress, 
    uploadResults, 
    currentFileIndex, 
    totalFilesCount 
  } = uploadState;

  const cancelUpload = () => {
    if (uploadState.uploadController) {
      uploadState.uploadController.abort();
    }
    updateUploadState({ isUploading: false });
  };

  const clearResults = useCallback(() => {
    updateUploadState({ 
      uploadResults: [],
      uploadProgress: {}
    });
  }, [updateUploadState]);

  const successCount = uploadResults.filter(r => r.status === 'success').length;
  const failedCount = uploadResults.filter(r => r.status === 'failed').length;
  const hasErrors = failedCount > 0;
  const hasResults = uploadResults.length > 0;

  const progressPercentage = totalFilesCount > 0 
    ? Math.round((currentFileIndex / totalFilesCount) * 100) 
    : 0;

  const failedFiles = uploadResults.filter(r => r.status === 'failed');

  useEffect(() => {
    if (!hasErrors && hasResults && !isUploading) {
      const timer = setTimeout(() => {
        clearResults();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [hasErrors, hasResults, isUploading, clearResults]);

  if (!isUploading && !hasResults) {
    return null;
  }

  const currentFileName = isUploading 
    ? Object.keys(uploadProgress).find(key => uploadProgress[key]?.status === 'uploading')
    : null;

  return (
    <div className={`${styles.wrapper} ${hasErrors ? styles.error : isUploading ? styles.uploading : styles.success}`}>
      {isUploading ? (
        <>
          <div className={styles.header}>
            <div className={styles.icon}>
              <Upload size={16} />
            </div>
            <div className={styles.title}>
              {t('upload.title')}
            </div>
            <button
              onClick={cancelUpload}
              className={styles.cancelButton}
            >
              {t('upload.buttonCancel')}
            </button>
          </div>
          
          <div className={styles.progressContainer}>
            <div className={styles.progressInfo}>
              <span className={styles.fileInfo}>
                {currentFileIndex} из {totalFilesCount}
              </span>
              <span className={styles.percentage}>
                {progressPercentage}%
              </span>
            </div>
            
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill} 
                style={{ width: `${progressPercentage}%` }} 
              />
            </div>

            {currentFileName && (
              <div className={styles.currentFile}>
                <span className={styles.fileName}>{currentFileName}</span>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className={cn(styles.header, { [styles.headerError]: hasErrors })}>
            <div className={styles.icon}>
              {hasErrors ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
            </div>
            <div className={styles.title}>
              {hasErrors ? t('upload.errorMessage') : t('upload.successMessage')}
            </div>
            <button
              onClick={clearResults}
              className={styles.closeButton}
            >
              <X size={16} />
            </button>
          </div>

          <div className={styles.message}>
            {hasErrors ? (
              <div className={styles.errorDetails}>
                <div className={styles.errorList}>
                  {failedFiles.map((result, index) => (
                    <div key={index} className={styles.errorItem}>
                      <span className={styles.errorFileName} title={result.file}>
                        {result.file}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={styles.successMessage}>
                {t('upload.finally', {count: successCount} )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};